'use client';

import { Rabbit, Scale, Gem } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { useIsLegacyTokenUser } from '@/shared/api/hooks/use-user';
import type { RenderEffort } from '@/types/render';
import { useRenderTokenPrices } from '../../hooks/use-render-token-prices';

export interface EffortContentProps {
  selectedEffort: RenderEffort;
  onEffortChange: (effort: RenderEffort) => void;
  // Layout variant — kept for API parity; both variants share the Apple-grouped
  // row idiom.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
}

export function EffortContent({
  selectedEffort,
  onEffortChange,
  closeOnSelect,
}: EffortContentProps) {
  const t = useTranslations('render.settings.effort');
  // Token prices are server-owned and demand-tuned; never use a local constant.
  const { data: tokenPrices } = useRenderTokenPrices();
  // "~N tokens" cost hints show ONLY to legacy users — pack / free users never
  // see a token number (P4.3). Server-driven via `tokens.is_legacy`. Mirrors
  // Apple EffortPickerView.showsTokenNumerics.
  const isLegacy = useIsLegacyTokenUser();

  const efforts: Array<{
    type: RenderEffort;
    icon: typeof Scale;
  }> = [
    { type: 'BRAINSTORM', icon: Rabbit },
    { type: 'STANDARD', icon: Scale },
    { type: 'HIGH', icon: Gem },
  ];

  const handleSelect = (effort: RenderEffort) => {
    onEffortChange(effort);
    closeOnSelect?.();
  };

  return (
    <div className="space-y-4">
      {/* Apple parity: grouped card with leading icon + title + description +
          "~N tokens" hint + trailing checkmark. */}
      <SettingsCard>
        {efforts.map((effort) => {
          const isSelected = selectedEffort === effort.type;
          const Icon = effort.icon;

          const leading = (
            <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          );

          // Hide the "~N tokens" suffix entirely until the server price loads —
          // no hardcoded fallback, a stale number would misrepresent cost — and
          // never show it to non-legacy users (P4.3).
          const tokenPrice = tokenPrices?.[effort.type];
          const baseDescription = t(`types.${effort.type}.description`);
          const description =
            tokenPrice === undefined || isLegacy !== true
              ? baseDescription
              : `${baseDescription} · ${t('tokenHint', { count: tokenPrice })}`;

          return (
            <PickerRow
              key={effort.type}
              title={t(`types.${effort.type}.name`)}
              description={description}
              leading={leading}
              isSelected={isSelected}
              onClick={() => handleSelect(effort.type)}
            />
          );
        })}
      </SettingsCard>
    </div>
  );
}

// Helper to get subtitle for preview
export function getEffortSubtitle(
  selectedEffort: RenderEffort,
  t: (key: string) => string
): string {
  return t(`types.${selectedEffort}.name`);
}
